import { useState, useRef } from 'react'
import { Grid, Paper, Typography, Box, Button } from '@mui/material'
import DownloadIcon from '@mui/icons-material/Download'
import ArrowForwardIcon from '@mui/icons-material/ArrowForward'
import html2canvas from 'html2canvas'
import jsPDF from 'jspdf'
import { Link } from 'react-router-dom'
import InfoTooltip from 'components/InfoTooltip'
import MemberTiersLevelChart from './MemberTiersLevelChart'
import CarRegistrationChart from './CarRegistrationChart'
import VoucherChart from './VoucherChart'
import { TimeRangeProvider, useTimeRange } from './TimeRangeContext'
import TimeRangeFilter from './TimeRangeFilter'
import PartnerVoucherChart from './PartnerVoucherChart'
import PartnerRedemptionRateChart from './PartnerRedemptionRateChart'
import BookingServiceChart from './BookingServiceChart'
import TransactionHistoryChart from './TransactionHistoryChart'
import CourtesyChart from './CourtesyChart'
import TradeInChart from './TradeInChart'
import RVGSubmissionChart from './RVGSubmissionChart'
import CarInterestHLCChart from './CarInterestHLCChart'
import SegmentAnalysisChart from './SegmentAnalysisChart'
import SegmentAnalysisTopCategoryChart from './SegmentAnalysisTopCategoryChart'
import SubSegmentAnalysisChart from './SubSegmentAnalysisChart'
import VoucherChannelDistributionChart from './VoucherChannelDistributionChart'
import TierFilter from './TierFilter'
import AnalyticsSummaryCards from './AnalyticsSummaryCards'
import ActiveUsersChart from './ActiveUsersChart'
import MostAccessedFeaturesChart from './MostAccessedFeaturesChart'

const SectionPanel = ({
  title,
  tooltip,
  viewDetailsPath,
  children
}: {
  title: string
  tooltip?: string
  viewDetailsPath?: string
  children: React.ReactNode
}) => (
  <Paper sx={{ p: 3, borderRadius: 2, boxShadow: 1, height: '100%' }}>
    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 3 }}>
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 0.75,
          pl: 1.5,
          borderLeft: '4px solid #002C5F'
        }}
      >
        <Typography variant='h6' sx={{ fontWeight: 600 }}>
          {title}
        </Typography>
        {tooltip && (
          <Box sx={{ marginTop: 1 }}>
            <InfoTooltip title={tooltip} iconSize={16} />
          </Box>
        )}
      </Box>
      {viewDetailsPath && (
        <Button
          component={Link}
          to={viewDetailsPath}
          variant='text'
          endIcon={<ArrowForwardIcon />}
          sx={{ textTransform: 'none' }}
          data-download-hide='true'
        >
          View Detail
        </Button>
      )}
    </Box>
    {children}
  </Paper>
)

const DashboardContent = () => {
  const { startDate, endDate } = useTimeRange()
  const [downloading, setDownloading] = useState(false)
  const [submissionTiers, setSubmissionTiers] = useState<string[]>([])
  const chartsRef = useRef<HTMLDivElement>(null)

  const handleDownload = async () => {
    if (!chartsRef.current) return
    setDownloading(true)

    const style = document.createElement('style')
    style.id = 'download-hide-style'
    style.textContent = '[data-download-hide="true"] { display: none !important; }'
    document.head.appendChild(style)

    const canvas = await html2canvas(chartsRef.current, { scale: 0.7, useCORS: true })

    document.head.removeChild(style)
    const imgData = canvas.toDataURL('image/png', 1)
    const pdf = new jsPDF({ orientation: 'portrait', unit: 'px', format: 'a4' })
    const pw = pdf.internal.pageSize.getWidth()
    const ph = pdf.internal.pageSize.getHeight()
    const imgH = (canvas.height * pw) / canvas.width
    let heightLeft = imgH
    let pos = 0
    pdf.addImage(imgData, 'PNG', 0, pos, pw, imgH)
    heightLeft -= ph
    while (heightLeft > 0) {
      pos = heightLeft - imgH
      pdf.addPage()
      pdf.addImage(imgData, 'PNG', 0, pos, pw, imgH)
      heightLeft -= ph
    }
    pdf.save(`dashboard-${startDate}-to-${endDate}.pdf`)
    setDownloading(false)
  }

  return (
    <>
      <Box sx={{ px: 4, pt: 4, pb: 1 }}>
        <Typography variant='h5' sx={{ fontWeight: 700, color: '#002C5F' }}>
          Analytics Dashboard
        </Typography>
      </Box>

      <Box
        sx={{ px: 4, py: 2, display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between' }}
      >
        <div data-download-hide='true'>
          <TimeRangeFilter />
        </div>
        <Button
          variant='contained'
          startIcon={<DownloadIcon />}
          onClick={handleDownload}
          disabled={downloading}
          sx={{ ml: 2, flexShrink: 0 }}
          data-download-hide='true'
        >
          {downloading ? 'Generating...' : 'Download PDF'}
        </Button>
      </Box>

      <Grid ref={chartsRef} container spacing={3} sx={{ p: 4 }}>
        <Grid item xs={12}>
          <SectionPanel title='Total Users' viewDetailsPath='/user/list'>
            <Grid container spacing={3}>
              <Grid item xs={12} md={4}>
                <Paper sx={{ borderRadius: 2, boxShadow: 1, overflow: 'hidden', height: '100%' }}>
                  <AnalyticsSummaryCards />
                </Paper>
              </Grid>
              <Grid item xs={12} md={4}>
                <ActiveUsersChart />
              </Grid>
              <Grid item xs={12} md={4}>
                <MemberTiersLevelChart />
              </Grid>
            </Grid>
          </SectionPanel>
        </Grid>

        <Grid item xs={12}>
          <Grid container spacing={3} alignItems='stretch'>
            <Grid item xs={12} md={5}>
              <SectionPanel title='Most Access Feature' tooltip='Displays the top features based on user access frequency.'>
                <MostAccessedFeaturesChart />
              </SectionPanel>
            </Grid>
            <Grid item xs={12} md={7}>
              <SectionPanel title='Car Registration' tooltip='Displays vehicle registration activity and approval outcomes' viewDetailsPath='/profiling/carinformation/list'>
                <CarRegistrationChart />
              </SectionPanel>
            </Grid>
          </Grid>
        </Grid>

        <Grid item xs={12}>
          <SectionPanel
            title='Service & Accessories'
            viewDetailsPath='/book-car-service-transaction'
          >
            <Grid container spacing={3}>
              <Grid item xs={12}>
                <BookingServiceChart />
              </Grid>
              <Grid item xs={6}>
                <TransactionHistoryChart />
              </Grid>
            </Grid>
          </SectionPanel>
        </Grid>

        <Grid item xs={12}>
          <SectionPanel
            title='Submission Feature'
          >
            <Box sx={{ maxWidth: 280, mb: 2 }} data-download-hide='true'>
              <TierFilter
                value={submissionTiers}
                onChange={setSubmissionTiers}
              />
            </Box>
            <Grid container spacing={3}>
              <Grid item xs={6}>
                <CourtesyChart memberTiers={submissionTiers.length > 0 ? submissionTiers.join(',') : undefined} />
              </Grid>
              <Grid item xs={6}>
                <TradeInChart memberTiers={submissionTiers.length > 0 ? submissionTiers.join(',') : undefined} />
              </Grid>
              <Grid item xs={6}>
                <RVGSubmissionChart />
              </Grid>
              <Grid item xs={6}>
                <CarInterestHLCChart />
              </Grid>
            </Grid>
          </SectionPanel>
        </Grid>

        <Grid item xs={12}>
          <SectionPanel title='Voucher' viewDetailsPath="/voucher/category">
            <Grid container spacing={3}>
              <Grid item xs={6}>
                <VoucherChart />
              </Grid>
              <Grid item xs={6}>
                <PartnerVoucherChart />
              </Grid>
              <Grid item xs={6}>
                <SegmentAnalysisChart />
              </Grid>
              <Grid item xs={6}>
                <SegmentAnalysisTopCategoryChart />
              </Grid>
              <Grid item xs={12}>
                <PartnerRedemptionRateChart />
              </Grid>
              <Grid item xs={6}>
                <SubSegmentAnalysisChart />
              </Grid>
              <Grid item xs={6}>
                <VoucherChannelDistributionChart />
              </Grid>
            </Grid>
          </SectionPanel>
        </Grid>
      </Grid>
    </>
  )
}

const Dashboard = () => (
  <Box sx={{ background: '#EFF1F4' }}>
    <TimeRangeProvider>
      <DashboardContent />
    </TimeRangeProvider>
  </Box>
)

export default Dashboard
